/**
 * Adds the faces framing can work out on its own to a story's speaker → faces
 * map (PLAT-12143), so a speaker nobody mapped is still framed on their face:
 *
 * - solo shots: a face alone on screen while one person talks (close-ups and
 *   cut-heavy edits, including a person re-tracked at every cut);
 * - leftover faces: the one face nobody else owns while a speaker talks
 *   (wide shots and panels).
 *
 * Deductions only fill gaps — a face already in the map keeps its speaker —
 * and leftover faces are worked out after solo shots, so they build on them.
 * A speaker who already has faces only gains more (their re-tracks) when the
 * deduction agrees with one of them; if it doesn't, the existing mapping is
 * likely a correction and wins.
 */

import { deduceFacesByElimination } from './deduceFacesByElimination';
import { deduceFacesFromSoloShots } from './deduceFacesFromSoloShots';

import type { EliminationDeduction } from './deduceFacesByElimination';
import type { SoloShotDeduction } from './deduceFacesFromSoloShots';
import type { TranscriptWord } from './speakerIntervalUtils';
import type { FaceDetection } from './types';

export interface DeducedFaces {
  /** The input map with deduced faces added. */
  map: Record<string, string[]>;
  /** face_label → speaker_label actually added, by source. */
  added: {
    soloShots: Record<string, string>;
    leftover: Record<string, string>;
  };
  soloShots: SoloShotDeduction;
  leftover: EliminationDeduction;
}

export function withDeducedFaces(
  speakerMap: Readonly<Record<string, readonly string[]>>,
  transcript: ReadonlyArray<TranscriptWord>,
  detections: ReadonlyArray<FaceDetection>,
): DeducedFaces {
  const map: Record<string, string[]> = {};
  const owned = new Set<string>();
  for (const [speakerLabel, faceLabels] of Object.entries(speakerMap)) {
    map[speakerLabel] = [...faceLabels];
    for (const faceLabel of faceLabels) owned.add(faceLabel);
  }

  const addAll = (faceToSpeaker: Record<string, string>) => {
    const added: Record<string, string> = {};
    for (const [faceLabel, speakerLabel] of Object.entries(faceToSpeaker)) {
      if (owned.has(faceLabel)) continue;
      const existing = speakerMap[speakerLabel] ?? [];
      const agrees = existing.some(
        known => faceToSpeaker[known] === speakerLabel,
      );
      if (existing.length > 0 && !agrees) continue;
      (map[speakerLabel] ??= []).push(faceLabel);
      owned.add(faceLabel);
      added[faceLabel] = speakerLabel;
    }
    return added;
  };

  const soloShots = deduceFacesFromSoloShots(transcript, detections);
  const addedSolo = addAll(soloShots.faceToSpeaker);
  const leftover = deduceFacesByElimination(transcript, detections, map);
  const addedLeftover = addAll(leftover.faceToSpeaker);

  return {
    map,
    added: { soloShots: addedSolo, leftover: addedLeftover },
    soloShots,
    leftover,
  };
}
